import { motion } from "framer-motion";
import {
  Layers,
  LineChart,
  ScrollText,
  Rocket,
  Network,
} from "lucide-react";
import { Reveal } from "./Reveal";

const columns = [
  {
    id: "financial-foundation",
    title: "Financial Foundation",
    Icon: Layers,
    accent: "#0a0a0a",
    items: ["Accounting cleanup", "Finance SOPs", "MIS setup", "Controls"],
  },
  {
    id: "strategic-finance",
    title: "Strategic Finance",
    Icon: LineChart,
    accent: "#15803d",
    items: ["Forecasting", "Budgeting", "Cash planning", "Pricing"],
  },
  {
    id: "investor-readiness",
    title: "Investor Readiness",
    Icon: ScrollText,
    accent: "#ea580c",
    items: [
      "Fundraise narrative",
      "Investor deck inputs",
      "Data room prep",
      "Diligence support",
    ],
  },
  {
    id: "venture-building",
    title: "Venture Building",
    Icon: Rocket,
    accent: "#7c3aed",
    items: [
      "Market positioning",
      "Business model design",
      "GTM roadmap",
      "Operating plan",
    ],
  },
  {
    id: "capital-network",
    title: "Capital & Network",
    Icon: Network,
    accent: "#1d4ed8",
    items: [
      "Inception cheques",
      "VC access",
      "Industry specialists",
      "RCS Advisors network",
    ],
  },
];

const WhatWeBring = () => {
  return (
    <section
      id="what-we-bring"
      data-testid="what-we-bring-section"
      className="relative py-24 md:py-32 border-t border-ink/15 bg-cream"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 md:mb-20">
          <div className="lg:col-span-7">
            <Reveal direction="up">
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink/60">
                What We Bring
              </span>
            </Reveal>
            <Reveal direction="up" delay={0.1}>
              <h2 className="mt-4 font-display font-black uppercase text-[clamp(2rem,5.4vw,4.5rem)] leading-[0.95] tracking-[-0.02em] text-ink">
                The full stack
                <br />
                <span className="italic font-medium normal-case text-money">we bring to the table.</span>
              </h2>
            </Reveal>
          </div>
        </div>

        {/* Grid: 5 columns on desktop, 1 on mobile */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-0 border-t border-l border-ink/20"
          data-testid="what-we-bring-grid"
        >
          {columns.map((col, idx) => {
            const Icon = col.Icon;
            return (
              <motion.div
                key={col.title}
                id={`wwb-${col.id}`}
                data-testid={`wwb-col-${idx}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: idx * 0.1, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -4 }}
                className="group relative bg-cream border-r border-b border-ink/20 p-7 md:p-8 flex flex-col gap-6 overflow-hidden scroll-mt-32 transition-shadow duration-500 target:shadow-[0_0_0_3px_var(--col-accent)]"
                style={{ "--col-accent": col.accent }}
              >
                {/* Top accent strip */}
                <span
                  aria-hidden
                  className="absolute top-0 left-0 h-0.5 w-0 group-hover:w-full transition-all duration-700"
                  style={{ backgroundColor: col.accent }}
                />

                {/* Icon */}
                <motion.div
                  className="relative h-12 w-12 flex items-center justify-center border border-ink/25 transition-colors duration-500 group-hover:border-current"
                  style={{ color: col.accent }}
                  whileHover={{ rotate: 6, scale: 1.06 }}
                  transition={{ type: "spring", stiffness: 220, damping: 14 }}
                >
                  <Icon className="w-6 h-6" strokeWidth={1.7} />
                </motion.div>

                {/* Column title */}
                <h3
                  className="font-display font-black uppercase text-lg md:text-xl leading-[1.05] tracking-[-0.01em] text-ink"
                  style={{ "--accent": col.accent }}
                >
                  {col.title}
                </h3>

                {/* Items */}
                <ul className="flex flex-col gap-3 mt-2">
                  {col.items.map((it, i) => (
                    <motion.li
                      key={it}
                      initial={{ opacity: 0, x: -8 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, amount: 0.2 }}
                      transition={{
                        duration: 0.4,
                        delay: idx * 0.1 + i * 0.06 + 0.2,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      className="flex items-start gap-2.5 font-sans-pro text-sm md:text-[15px] leading-snug text-ink/85"
                    >
                      <span
                        className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full transition-transform duration-300 group-hover:scale-150"
                        style={{ backgroundColor: col.accent }}
                      />
                      <span>{it}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {/* Footnote */}
        <Reveal direction="up" delay={0.3}>
          <p className="mt-10 md:mt-14 font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 text-center">
            Every engagement is shaped around what your stage actually needs.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default WhatWeBring;
